import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { KompisSheetFooterButtons } from '@inter-ikea-kompis/react-components';
import SlideOutMenu from 'components/SlideOutMenu/SlideOutMenu';
import { useCore } from 'contexts/core';
import { useAppDispatch } from 'ducks/hooks';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { undoRoomSize } from 'ducks/roomSizeHistory';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Box from 'ui/Box';
import Body from 'ui/Typography/Body';
import Title from 'ui/Typography/Title';
import { InvalidPlacementComponent } from '@inter-ikea-storageone/range';
import { hasInvalidFramePlacement, setInvalidFramePlacement } from 'ducks/layout';
import { useCallback, useEffect } from 'react';

const FooterButtons = styled.div`
  * {
    padding-bottom: ${({ theme }) => theme.space.md};
  }
`;

const VerticalScroll = styled.div`
  height: inherit;
  overflow: auto;
`;

const Content = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: scroll;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const FooterWrapper = styled.div`
  padding: 1.5rem 3rem 0.5rem 3rem;
  position: sticky;
  width: 100%;
  background: white;
  bottom: 0;
  border-top: 0.0625rem solid #dfdfdf;
`;

const InvalidPlacementsMenu = () => {
  const coreApi = useCore();
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const invalidPlacementMenuVisible = useSelector(hasInvalidFramePlacement);

  const onDelete = () => {
    coreApi.history.historyAction(() => {
      const invalidPlacedFrames = coreApi.entities.getByComponent(InvalidPlacementComponent.id);
      coreApi.entities.deleteMultiple(invalidPlacedFrames);
      validateFramePlacement();
    });
  };

  const onUndo = () => {
    dispatch(undoRoomSize(coreApi));
    validateFramePlacement();
  };

  const validateFramePlacement = useCallback(() => {
    if (invalidPlacementMenuVisible) {
      const invalidPlacedFrames = coreApi.entities
        .getByComponent(InvalidPlacementComponent.id)
        .map(entity => entity.id);
      if (invalidPlacedFrames.length === 0) {
        dispatch(setInvalidFramePlacement(false));
      }
    }
  }, [coreApi.entities, dispatch, invalidPlacementMenuVisible]);

  useEffect(() => {
    coreApi
      .getServices()
      .entityManager.subscribeRemove(InvalidPlacementComponent.id, validateFramePlacement);
    return () => {
      coreApi
        .getServices()
        .entityManager.unsubscribeRemove(InvalidPlacementComponent.id, validateFramePlacement);
    };
  }, [coreApi, validateFramePlacement]);

  return (
    <SlideOutMenu trigger={invalidPlacementMenuVisible}>
      <VerticalScroll>
        <Box height={'100%'}>
          <Content>
            <Box padding={'1.5rem 3rem'}>
              <Title type={ThemeFontStyleTypeEnum.headingMedium} pb="l" paddingTop={'0.5rem'}>
                {formatMessage({ id: 'storageone.invalidPlacementMenuHeading' })}
              </Title>
              <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
                {formatMessage({ id: 'storageone.invalidPlacementMenuBody' })}
              </Body>
            </Box>
            <FooterWrapper>
              <FooterButtons>
                <KompisSheetFooterButtons
                  compact={false}
                  theme={SkapaTheme}
                  primaryLabel={formatMessage({
                    id: 'storageone.invalidPlacementMenuButtonDelete'
                  })}
                  onButtonClick={onDelete}
                />
                <KompisSheetFooterButtons
                  compact={false}
                  theme={SkapaTheme}
                  secondaryLabel={formatMessage({
                    id: 'storageone.invalidPlacementMenuButtonUndo'
                  })}
                  onButtonClick={onUndo}
                />
              </FooterButtons>
            </FooterWrapper>
          </Content>
        </Box>
      </VerticalScroll>
    </SlideOutMenu>
  );
};

export default InvalidPlacementsMenu;
